import { HttpResponse, HttpStatusEnum } from '@core/api';
import { OrderStatusEnum } from '@shared/enums';
import {
    EstimatedAmount,
    EstimatedAmountDTO,
    InitialPrepareStepThree,
    InitialPrepareStepThreeDTO,
    PrepareStepThreeByUser,
    PrepareStepThreeByUserDTO,
    PrimaryBillingAddress,
    RefreshBillingAddressDTO,
} from '@shared/interfaces';
import { ShippingMethodsEnum } from '../../../_shared/_enums/shipping-methods.enum';
import {
    DraftOrderDTO,
    ExcelOrderDTO,
    ExcelOrderData,
    Order,
    PrepareOrderSummaryPayload,
    PrepareStepTwo,
    PrepareStepTwoDTO,
    Reaction,
    SaveDraftPayload,
    StepTwoSampleData,
    StepTwoSampleDataDTO,
    SubmitOrderDTO,
    SubmitOrderPayload,
    SubmitOrderResponse,
    ValidateNumberOfSamples,
    ValidateNumberOfSamplesDTO,
    ValidateReactionDetails,
    ValidateReactionDetailsDTO,
} from '../interfaces';

export class OrderSetupMapper {
    static mapPrepareStepTwoDTOtoModel(prepareStepTwoDTO: PrepareStepTwoDTO): HttpResponse<PrepareStepTwo> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                customPrimers: prepareStepTwoDTO.customPrimers.map(primer => {
                    return {
                        name: primer.name,
                        sequence: primer.sequence,
                        status: primer.status,
                    };
                }),
                standardPrimers: prepareStepTwoDTO.standardPrimers.map(primer => {
                    return {
                        name: primer.name,
                        sequence: primer.sequence,
                    };
                }),
            },
        };
    }

    static mapValidateNumberOfReactionsDTOtoModel(
        validateNumberOfReactionsDTO: ValidateNumberOfSamplesDTO
    ): HttpResponse<ValidateNumberOfSamples> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                barcodeErrors: validateNumberOfReactionsDTO.validationErrors.invalidBarcodes.map(
                    barcodeError => barcodeError
                ),
                nameErrors: validateNumberOfReactionsDTO.validationErrors.usedNames.map(nameError => nameError),
                sequenceErrors: validateNumberOfReactionsDTO.validationErrors.usedSequences.map(
                    sequenceError => sequenceError
                ),
                usedBarcodes: validateNumberOfReactionsDTO.validationErrors.usedBarcodes.map(
                    usedBarcode => usedBarcode
                ),
            },
        };
    }

    static mapValidateReactionDetailsDTOtoModel(
        validateReactionDetailsDTO: ValidateReactionDetailsDTO
    ): HttpResponse<ValidateReactionDetails> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                existingReactions: validateReactionDetailsDTO.validationErrors.existingReactions.map(
                    existingReaction => {
                        return {
                            primers: existingReaction.primers,
                            sampleBarcode: existingReaction.sampleBarcode,
                        };
                    }
                ),
                expiredSamples: validateReactionDetailsDTO.validationErrors.expiredSamples.map(
                    expiredSample => expiredSample
                ),
                invalidBarcodes: validateReactionDetailsDTO.validationErrors.invalidBarcodes.map(
                    invalidBarcodes => invalidBarcodes
                ),
                usedBarcodes: validateReactionDetailsDTO.validationErrors.usedBarcodes.map(userBarcode => userBarcode),
                unreceivedSamples: validateReactionDetailsDTO.validationErrors.unreceivedSamples.map(
                    unreceivedSample => unreceivedSample
                ),
                disposedSamples: validateReactionDetailsDTO.validationErrors.disposedSamples.map(
                    disposedSample => disposedSample
                ),
                emptySamples: validateReactionDetailsDTO.validationErrors.emptySamples.map(emptySample => emptySample),
                contaminatedSamples: validateReactionDetailsDTO.validationErrors.contaminatedSamples.map(
                    contaminatedSample => contaminatedSample
                ),
                tubeMissingSamples: validateReactionDetailsDTO.validationErrors.tubeMissingSamples.map(
                    tubeMissingSample => tubeMissingSample
                ),
                areOutOfStockPrimersSelected: validateReactionDetailsDTO.validationErrors.outOfStockPrimers,
            },
        };
    }

    static mapRefreshBillingAddressDTOtoModel(
        refreshBillingAddressDTO: RefreshBillingAddressDTO
    ): HttpResponse<PrimaryBillingAddress | null> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: refreshBillingAddressDTO.defaultBillingAddress
                ? {
                      addressLine1: refreshBillingAddressDTO.defaultBillingAddress.addressLine1,
                      addressLine2: refreshBillingAddressDTO.defaultBillingAddress.addressLine2,
                      addressLine3: refreshBillingAddressDTO.defaultBillingAddress.addressLine3,
                      addressName: refreshBillingAddressDTO.defaultBillingAddress.addressName,
                      city: refreshBillingAddressDTO.defaultBillingAddress.city,
                      country: refreshBillingAddressDTO.defaultBillingAddress.country,
                      id: refreshBillingAddressDTO.defaultBillingAddress.id,
                      isDefault: refreshBillingAddressDTO.defaultBillingAddress.isDefault,
                      postalCode: refreshBillingAddressDTO.defaultBillingAddress.postalCode,
                      regionCode: refreshBillingAddressDTO.defaultBillingAddress.regionCode,
                  }
                : null,
        };
    }

    static mapPrepareStepThreeByUserDTOtoModel(dto: PrepareStepThreeByUserDTO): HttpResponse<PrepareStepThreeByUser> {
        return {
            data: {
                paymentMethods: dto.paymentMethods.map(paymentMethod => {
                    return {
                        hasValue: paymentMethod.hasValue,
                        isDefault: paymentMethod.isDefault,
                        name: paymentMethod.name,
                        paymentMethodId: paymentMethod.paymentMethodId,
                        values: paymentMethod.values.map(value => {
                            return {
                                paymentMethodValueId: value.paymentMethodValueId,
                                value: value.value,
                                isDefault: value.isDefault,
                            };
                        }),
                    };
                }),
                primaryBillingAddress: dto.defaultBillingAddress
                    ? {
                          addressLine1: dto.defaultBillingAddress.addressLine1,
                          addressLine2: dto.defaultBillingAddress.addressLine2,
                          addressLine3: dto.defaultBillingAddress.addressLine3,
                          addressName: dto.defaultBillingAddress.addressName,
                          city: dto.defaultBillingAddress.city,
                          country: dto.defaultBillingAddress.country,
                          id: dto.defaultBillingAddress.id,
                          isDefault: dto.defaultBillingAddress.isDefault,
                          postalCode: dto.defaultBillingAddress.postalCode,
                          regionCode: dto.defaultBillingAddress.regionCode,
                      }
                    : null,
            },
            status: HttpStatusEnum.SUCCESS,
        };
    }

    static mapInitialPrepareStepThreeDTOtoModel(
        dto: InitialPrepareStepThreeDTO
    ): HttpResponse<InitialPrepareStepThree> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                requesters: dto.groupUsers.map(user => {
                    return {
                        email: user.email,
                        fullName: `${user.firstName} ${user.lastName}`,
                        id: user.id,
                        lab: user.lab,
                        organization: user.organization,
                    };
                }),
                shippingMethod: dto.shippingMethod,
                pickUpLocations: dto.pickupCouriers.map(pickup => {
                    return {
                        id: pickup.id,
                        name: pickup.name,
                        price: pickup.price,
                    };
                }),
                couriers: dto.couriers.map(courier => {
                    return {
                        id: courier.id,
                        name: courier.name,
                    };
                }),
                informedUsers: dto.informedUserOptions.map(informedUser => {
                    return {
                        email: informedUser.email,
                        id: informedUser.id,
                        fullName: `${informedUser.firstName} ${informedUser.lastName}`,
                    };
                }),
                serviceLevel: {
                    hours: dto.serviceLevels[0].hours,
                    id: dto.serviceLevels[0].id,
                    name: dto.serviceLevels[0].name,
                },
                hasShipping: dto.hasShipping,
            },
        };
    }

    static mapSubmitOrderPayload(order: Order, shippingMethod: ShippingMethodsEnum): SubmitOrderPayload {
        return {
            id: order.id,
            punchoutPayloadId: order.punchoutPayloadId,
            step1: {
                recentCustomPrimers: order.orderRecentCustomPrimers.map(recentPrimer => {
                    return { name: recentPrimer.name, sequence: recentPrimer.sequence };
                }),
                recentShippedPrimers: order.orderShippedPrimers.map(shippedPrimer => {
                    return { name: shippedPrimer.name, barcode: shippedPrimer.barcode };
                }),
            },
            step2: {
                samples: order.reactions.map(reaction => {
                    return {
                        name: reaction.sampleName!,
                        barcode: reaction.barcode!,
                        size: reaction.size!,
                        dnaType: reaction.dnaType!,
                        standardPrimerNames: reaction.standardPrimers.map(standardPrimer => standardPrimer) || [],
                        customPrimerNames: reaction.customPrimers.map(customPrimer => customPrimer) || [],
                        recentCustomPrimerNames:
                            reaction.recentCustomPrimers.map(recentCustomPrimer => recentCustomPrimer) || [],
                        recentShippedPrimerNames:
                            reaction.shippedPrimers.map(recentShippedPrimer => recentShippedPrimer) || [],
                    };
                }),
            },
            step3: {
                name: order.orderName,
                omRequesterUserId: order.requesterUserId,
                informedUserIds: order.informedUserIds,
                pickupCourierId: order.pickUpCourierId,
                selectedOwnCourier:
                    shippingMethod === ShippingMethodsEnum.SHIPPING_MY_OWN && order.ownCourierId != null
                        ? {
                              courierId: order.ownCourierId!,
                              courierName: order.ownCourierId === 0 ? order.ownCourierName : null,
                              trackingNumber: order.trackingNumber,
                          }
                        : null,
                serviceLevelId: order.serviceLevelId!,
                omPaymentMethodId: order.paymentMethodId,
                omPaymentMethodValueId: order.paymentMethodValue?.paymentMethodValueId || null,
                omPaymentMethodValue: order.paymentMethodValue?.paymentMethodValueId
                    ? null
                    : order.paymentMethodValue?.value || null,
            },
        };
    }

    static mapAddInformedUserDTOtoModel(addInformedUserDTO: { id: number }): HttpResponse<{ id: number }> {
        return {
            data: {
                id: addInformedUserDTO.id,
            },
            status: HttpStatusEnum.SUCCESS,
        };
    }

    static mapEstimatedAmountDTOtoModel(estimatedAmount: EstimatedAmountDTO): HttpResponse<EstimatedAmount> {
        return {
            data: {
                reactionsCount: estimatedAmount.orderEstimate.reactionsCount,
                shipping: estimatedAmount.orderEstimate.shipping,
                subtotal: estimatedAmount.orderEstimate.subtotal,
                total: estimatedAmount.orderEstimate.total,
                reactionEstimates: estimatedAmount.orderEstimate.sampleEstimates.map(estimation => {
                    return {
                        primerPrice: estimation.primerPrice,
                        primersCount: estimation.primersCount,
                        total: estimation.total,
                        sampleName: estimation.name,
                    };
                }),
            },
            status: HttpStatusEnum.SUCCESS,
        };
    }

    static mapDraftOrderDTOtoModel(draftOrder: DraftOrderDTO): HttpResponse<Order> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                ownCourierId: draftOrder.step3.selectedOwnCourier?.courierId ?? null,
                ownCourierName: draftOrder.step3.selectedOwnCourier?.courierName ?? null,
                id: draftOrder.id,
                informedUserIds: draftOrder.step3.informedUserIds ?? [],
                numberOfReactions: draftOrder.step2.samples.length || 1,
                orderName: draftOrder.step3.name ?? null,
                orderRecentCustomPrimers: (draftOrder.step1.recentCustomPrimers || []).map(recentCustomPrimer => {
                    return {
                        name: recentCustomPrimer.name,
                        sequence: recentCustomPrimer.sequence ?? '',
                    };
                }),
                orderRecentStandardPrimers: [],
                orderShippedPrimers: (draftOrder.step1.recentShippedPrimers || []).map(shippedPrimer => {
                    return {
                        name: shippedPrimer.name,
                        barcode: shippedPrimer.barcode,
                    };
                }),
                paymentMethodId: draftOrder.step3.omPaymentMethodId ?? null,
                paymentMethodValue: draftOrder.step3.omPaymentMethodValue
                    ? {
                          isDefault: false,
                          paymentMethodValueId: draftOrder.step3.omPaymentMethodValueId ?? 0,
                          value: draftOrder.step3.omPaymentMethodValue,
                      }
                    : null,
                reactions: OrderSetupMapper.mapDraftReactionDTOtoModel(draftOrder),
                requesterUserId: draftOrder.step3.omRequesterUserId ?? null,
                status: OrderStatusEnum.INCOMPLETE,
                trackingNumber: draftOrder.step3.selectedOwnCourier?.trackingNumber ?? null,
                pickUpCourierId: draftOrder.step3.pickupCourierId,
                serviceLevelId: draftOrder.step3.serviceLevelId,
                punchoutPayloadId: draftOrder.punchoutPayloadId,
            },
        };
    }

    static mapSubmitOrderDTOToModel(submitOrderDTO: SubmitOrderDTO): HttpResponse<SubmitOrderResponse> {
        return {
            data: {
                id: submitOrderDTO.id,
                punchoutUrl: submitOrderDTO.punchoutUrl,
                punchoutCXml: submitOrderDTO.punchoutCXml || null,
            },
            status: HttpStatusEnum.SUCCESS,
        };
    }

    static mapDraftReactionDTOtoModel(draftOrder: DraftOrderDTO): Reaction[] {
        return draftOrder.step2.samples.map(reaction => {
            return {
                barcode: reaction.barcode,
                customPrimers: reaction.customPrimerNames,
                dnaType: reaction.dnaType,
                recentCustomPrimers: reaction.recentCustomPrimerNames,
                sampleName: reaction.name,
                shippedPrimers: reaction.recentShippedPrimerNames,
                size: reaction.size,
                standardPrimers: reaction.standardPrimerNames,
                isUsedSample: reaction.locked,
                recentStandardPrimers: [],
            };
        });
    }

    static mapExcelOrderDTOtoModel(excelOrder: ExcelOrderDTO): HttpResponse<ExcelOrderData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                orderRecentCustomPrimers:
                    excelOrder.step1.recentCustomPrimers.map(customPrimer => {
                        return { name: customPrimer.name, sequence: customPrimer.sequence };
                    }) ?? [],
                orderShippedPrimers:
                    excelOrder.step1.recentShippedPrimers.map(shippedPrimer => {
                        return {
                            barcode: shippedPrimer.barcode,
                            name: shippedPrimer.name,
                        };
                    }) ?? [],
                reactions:
                    excelOrder.step2.samples.map(excelReaction => {
                        return {
                            barcode: excelReaction.barcode,
                            customPrimers: excelReaction.customPrimerNames,
                            dnaType: excelReaction.dnaType,
                            recentCustomPrimers: excelReaction.recentCustomPrimerNames,
                            sampleName: excelReaction.name,
                            shippedPrimers: excelReaction.recentShippedPrimerNames,
                            size: excelReaction.size,
                            standardPrimers: excelReaction.standardPrimerNames,
                            recentStandardPrimers: [],
                            isUsedSample: excelReaction.locked,
                        };
                    }) ?? [],
                navigateTo: excelOrder.redirectTo ?? 1,
                samplesMismatch: excelOrder.samplesMismatch,
            },
        };
    }

    static mapOrderToSaveDraftPayload(order: Order): SaveDraftPayload {
        return {
            id: order.id ?? null!,
            punchoutPayloadId: order.punchoutPayloadId,
            step1: {
                recentCustomPrimers: (order.orderRecentCustomPrimers ?? []).filter(recentPrimer => {
                    return recentPrimer.name || recentPrimer.sequence;
                }),
                recentShippedPrimers: (order.orderShippedPrimers ?? []).filter(shippedPrimer => {
                    return shippedPrimer.barcode || shippedPrimer.name;
                }),
            },
            step2: {
                samples: order.reactions
                    .filter(reaction => {
                        return (
                            reaction.barcode ||
                            reaction.dnaType !== null ||
                            reaction.sampleName ||
                            reaction.size ||
                            reaction.customPrimers.length ||
                            reaction.recentCustomPrimers.length ||
                            reaction.shippedPrimers.length ||
                            reaction.standardPrimers.length
                        );
                    })
                    .map(reaction => {
                        return {
                            customPrimerNames: reaction.customPrimers,
                            dnaType: reaction.dnaType ?? null,
                            name: reaction.sampleName ?? null,
                            barcode: reaction.barcode || null,
                            size: reaction.size ?? null,
                            standardPrimerNames: [...reaction.standardPrimers, ...reaction.recentStandardPrimers],
                            recentCustomPrimerNames: reaction.recentCustomPrimers,
                            recentShippedPrimerNames: reaction.shippedPrimers,
                        };
                    }),
            },
            step3: {
                informedUserIds: order.informedUserIds ?? [],
                name: order.orderName || null,
                omPaymentMethodId: order.paymentMethodId ?? null,
                omPaymentMethodValue: order.paymentMethodValue?.paymentMethodValueId
                    ? null
                    : (order.paymentMethodValue?.value ?? null),
                omPaymentMethodValueId: order.paymentMethodValue?.paymentMethodValueId || null,
                omRequesterUserId: order.requesterUserId ?? null,
                pickupCourierId: order.pickUpCourierId ?? null,
                selectedOwnCourier:
                    order.ownCourierId != null
                        ? {
                              courierId: order.ownCourierId ?? null,
                              courierName: order.ownCourierName ?? null,
                              trackingNumber: order.trackingNumber ?? null,
                          }
                        : null,
                serviceLevelId: order.serviceLevelId ?? null,
            },
        };
    }

    static mapSaveDraftDTOtoModel(response: { id: number }): HttpResponse<{ id: number }> {
        return {
            data: { id: response.id },
            status: HttpStatusEnum.SUCCESS,
        };
    }

    static mapOrderForPrepareOrderSummary(order: Order): PrepareOrderSummaryPayload {
        return {
            step1: {
                recentCustomPrimers: (order.orderRecentCustomPrimers ?? []).filter(recentPrimer => {
                    return recentPrimer.name || recentPrimer.sequence;
                }),
                recentShippedPrimers: (order.orderShippedPrimers ?? []).filter(shippedPrimer => {
                    return shippedPrimer.barcode || shippedPrimer.name;
                }),
            },
            step2: {
                samples: order.reactions
                    .filter(reaction => {
                        return (
                            reaction.barcode ||
                            reaction.dnaType !== null ||
                            reaction.sampleName ||
                            reaction.size ||
                            reaction.customPrimers.length ||
                            reaction.recentCustomPrimers.length ||
                            reaction.shippedPrimers.length ||
                            reaction.standardPrimers.length
                        );
                    })
                    .map(reaction => {
                        return {
                            customPrimerNames: reaction.customPrimers,
                            dnaType: reaction.dnaType ?? null,
                            name: reaction.sampleName ?? null,
                            barcode: reaction.barcode || null,
                            size: reaction.size ?? null,
                            standardPrimerNames: reaction.standardPrimers,
                            recentCustomPrimerNames: reaction.recentCustomPrimers,
                            recentShippedPrimerNames: reaction.shippedPrimers,
                        };
                    }),
            },
        };
    }

    static mapSampleDataDTOToModel(data: StepTwoSampleDataDTO): HttpResponse<StepTwoSampleData[]> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: data.samples.map(sample => {
                return {
                    name: sample.name,
                    size: sample.size,
                    barcode: sample.barcode,
                    dnaType: sample.dnaType,
                    primers: sample.primers,
                    status: sample.status,
                };
            }),
        };
    }
}
